import {
  faCheck,
  faComment,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import AnalyticsUpIcon from '../../../../../assets/icons/analytics-up-stroke-rounded (1)'
import ArrowDataTransferHorizontalIcon from '../../../../../assets/icons/arrow-data-transfer-horizontal-stroke-rounded (1)'
import BankIcon from '../../../../../assets/icons/bank-stroke-rounded'
import CheckmarkCircle02Icon from '../../../../../assets/icons/checkmark-circle-02-stroke-rounded'
import Wallet03Icon from '../../../../../assets/icons/wallet-03-stroke-rounded'
import PageWrapper from '../../../../../components/Pagewrapper'
import {
  useGetAllBankAccountsByUserIdQuery,
  useGetAllSwapsByIdQuery,
  useGetAllWalletsByUserIdQuery,
  useGetFundDepositsQuery,
} from '../../../../../redux/services/coreApi'
import type { RootState } from '../../../../../redux/store'

type CompletedProps = {}

const Completed: React.FC<CompletedProps> = ({}) => {
  const history = useHistory()

  const sessionUser = useSelector((state: RootState) => state.session.user)

  const { data: walletsData } = useGetAllWalletsByUserIdQuery({
    userId: sessionUser.id,
  })
  const { data: bankAccountsData } = useGetAllBankAccountsByUserIdQuery({
    userId: sessionUser.id,
  })
  const { data: swapsData } = useGetAllSwapsByIdQuery({
    userId: sessionUser.id,
  })
  const { data: fundDepositsData } = useGetFundDepositsQuery({
    investorId: sessionUser.id,
  })

  // Veli-migrated clients (ADR-0004) don't have the Investing product yet — their
  // portfolio is still being replicated on Penning — so we hide the "invest" CTA
  // and surface a short explainer instead (matches the greyed-out menu items).
  const isVeliMigrating = sessionUser.isVeliOrigin ?? false

  // The assigned wealth manager's display name comes off the session user
  // (UserDto.wealthManagerName). Null for a direct client with no WM, in which
  // case we drop the reassurance card rather than show an empty one. Initials
  // are the first letter of the first two name parts (e.g. "Nataša Kozlevčar"
  // → "NK"), the avatar fallback while there's no photo.
  const wealthManagerName = sessionUser.wealthManagerName ?? null
  const wealthManagerInitials = wealthManagerName
    ? wealthManagerName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('')
    : ''

  // The "all set / holding" screen — what a migrating Veli client sees once the
  // checklist is done. There's deliberately nothing to do here and no platform
  // CTAs (those surfaces are locked until the move): the job of this screen is
  // to turn the wait into something legible — the account is now with our
  // compliance team, and once that review clears the client is reactivated on
  // Veli and can resume trading; the move to Penning follows later in 2026.
  // Dates are kept soft on purpose (the earlier hard dates went stale); see the
  // Veli migration thread / ADR-0004.
  if (isVeliMigrating) {
    return (
      // embedded: VeliOnboardingShell already owns the screen height and the one
      // scroll context (it stacks the co-brand header above this). Without it,
      // PageWrapper claims its own overflow and the page gets a second, nested
      // scrollbar around the card.
      <PageWrapper fixedWidth embedded>
        <div className="bg-white border border-solid border-black/10 rounded-3xl p-8 md:p-14 mt-4 flex flex-col items-center text-center">
          <div className="h-16 w-16 rounded-full bg-[#2E9E6B]/15 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faCheck}
              className="text-[#2E9E6B] h-6 w-6"
            />
          </div>
          <h1 className="text-2xl md:text-3xl font-semi mt-6">
            You're all set
          </h1>
          <p className="text-description mt-2 max-w-md">
            We've received your acceptance. Our{' '}
            <span className="font-semi text-black/70">compliance team</span> is
            now reviewing your account — as soon as that's done, you'll be
            activated again on Veli and can resume trading as usual.
          </p>

          <div className="w-full mt-10 rounded-2xl border border-solid border-black/10 p-6 text-left">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-black/45 mb-6">
              What happens next
            </p>

            <div className="flex items-start">
              {/* Now — acceptance received */}
              <div className="flex flex-col items-center flex-1 min-w-0 text-center">
                <div className="h-3 w-3 rounded-full bg-[#2E9E6B]" />
                <p className="font-semi text-sm mt-3">Now</p>
                <p className="text-description text-xs mt-0.5">
                  Agreement accepted
                </p>
              </div>
              <div className="flex-1 h-[1px] bg-black/10 mt-1.5" />
              {/* Compliance review — the current, active step */}
              <div className="flex flex-col items-center flex-1 min-w-0 text-center">
                <div className="h-3 w-3 rounded-full bg-[#B5523C]" />
                <p className="font-semi text-sm mt-3">Compliance review</p>
                <p className="text-description text-xs mt-0.5">
                  We're reviewing your account
                </p>
              </div>
              <div className="flex-1 h-[1px] bg-black/10 mt-1.5" />
              {/* Move to Penning — later in the year */}
              <div className="flex flex-col items-center flex-1 min-w-0 text-center">
                <div className="h-3 w-3 rounded-full border-2 border-solid border-black/20" />
                <p className="font-semi text-sm mt-3">Later in 2026</p>
                <p className="text-description text-xs mt-0.5">
                  Your portfolio moves to Penning
                </p>
              </div>
            </div>

            <div className="h-[1px] bg-black/5 my-6" />
            <p className="text-description">
              Right now your account is on hold while our compliance team
              completes its review. As soon as the review is done, we'll{' '}
              <span className="font-semi">reactivate your account on Veli</span>{' '}
              so you can pick up trading right where you left off — same app,
              same holdings, same strategy. Later in 2026 your portfolio will
              move to Penning and the Veli app becomes the Penning app. We'll
              email you at each step, so there's nothing you need to do for now.
            </p>
          </div>

          <div className="w-full mt-6 rounded-2xl bg-bg-component p-5 flex items-start gap-3 text-left">
            <FontAwesomeIcon
              icon={faShieldHalved}
              className="mt-0.5 shrink-0 text-black/50"
            />
            <p className="text-description">
              <span className="font-semi text-black/70">
                Your investments stay exactly as they are
              </span>{' '}
              — same holdings, same strategy. The only change is who safeguards
              your portfolio behind the scenes, and that won't happen until the
              move to Penning later in 2026.
            </p>
          </div>

          {wealthManagerName ? (
            <div className="w-full mt-3.5 rounded-2xl border border-solid border-black/10 p-4 flex items-center gap-3 text-left">
              <div className="h-9 w-9 shrink-0 rounded-full bg-bg-component flex items-center justify-center text-xs font-semi text-black/60">
                {wealthManagerInitials}
              </div>
              <div className="flex-1 min-w-0 leading-tight">
                <p className="text-sm font-semi">{wealthManagerName}</p>
                <p className="text-description text-xs">
                  Your wealth manager — still looking after your money
                </p>
              </div>
              <FontAwesomeIcon
                icon={faComment}
                className="shrink-0 text-black/30"
              />
            </div>
          ) : null}

          <p className="text-description text-sm mt-8">
            Questions? Write{' '}
            <a
              href="mailto:support@penning.io"
              className="hover:underline text-blue-600"
            >
              support@penning.io
            </a>
            .
          </p>
        </div>
      </PageWrapper>
    )
  }

  const actions = [
    {
      title: 'Add a Bank Account',
      onClick: () => history.push('/app/bank-accounts'),
      icon: <BankIcon className="h-5 w-5 text-inherit" />,
      isFulfilled: bankAccountsData?.length,
    },
    {
      title: 'Add a Crypto Wallet',
      onClick: () => history.push('/app/wallets'),
      icon: <Wallet03Icon className="h-5 w-5 text-inherit" />,
      isFulfilled: walletsData?.length,
    },
    {
      title: 'Make a swap',
      onClick: () => history.push('/app/broker-service'),
      icon: (
        <ArrowDataTransferHorizontalIcon className="h-5 w-5 text-inherit" />
      ),
      isFulfilled: swapsData?.length,
    },
    {
      title: 'Invest in our Capital Management fund',
      onClick: () => history.push('/app/investor'),
      icon: <AnalyticsUpIcon className="h-5 w-5 text-inherit" />,
      isFulfilled: fundDepositsData?.length,
    },
  ]

  return (
    <PageWrapper fixedWidth>
      <div className="bg-white border border-solid border-black/10 rounded-3xl p-14 flex flex-col gap-1.5 mt-20">
        <div className="mb-8">
          <p className="text-xl font-semi mb-2">Getting started with Penning</p>
          <p className="text-description">
            Welcome {sessionUser.name}! Your account setup is done and you are
            now ready to get the most out of Penning by completing the following
            tasks.
          </p>
        </div>

        {actions.map((item, i) => {
          return (
            <button
              disabled={!!item.isFulfilled}
              onClick={item.onClick}
              className={`flex items-center justify-between px-4 py-3.5   select-none rounded-lg disabled:text-black/50 ${
                item.isFulfilled
                  ? 'bg-gray-50 '
                  : 'hover:bg-gray-100 border border-solid border-black/10 cursor-pointer'
              }`}
              key={i}
            >
              <div className="flex items-center gap-2 font-medium text-sm">
                {item.icon}
                {item.title}
              </div>
              {item.isFulfilled ? (
                <CheckmarkCircle02Icon className="w-5 h-5 text-white bg-green-700 rounded-full" />
              ) : undefined}
            </button>
          )
        })}
        <p className="text-description mt-10">
          Do you have other questions concerning how to get started?{' '}
          <a
            href="mailto:support@penning.io"
            target="_blank"
            className="hover:underline text-blue-600"
            rel="noreferrer"
          >
            Click here
          </a>
        </p>
      </div>
    </PageWrapper>
  )
}

export default Completed
